import { usePhotoUpload } from "@/composables/usePhotoUpload";
import { coordsToGeoJSON } from "@/utils/map";
import { CreateNoteDTO, EntityType } from "seedgreen-shared/dtos/NoteDTO";
import {
	useCreateNote,
	useGetNotePhotoUploadUrls,
	useAddPhotosToNote,
} from "seedgreen-shared/queries/note";
import { reactive, computed, Ref } from "vue";
import { sendToast } from "@/utils/sendToast";
import { captureException } from "@sentry/vue";
import { PhotoItem } from "seedgreen-shared/types/photo.types";
import { useI18n } from "vue-i18n";
import { NoteObservationDTO } from "seedgreen-shared/dtos/NoteObservationDTO";

export interface NoteFormData {
	plantingIds: number[];
	description: string;
	photos: PhotoItem[];
	coordinates: number[][];
	isActionRequired: boolean;
	observations: NoteObservationDTO[];
}

export interface NoteFormContext {
	formData: NoteFormData;
	isValid: Ref<boolean>;
	isLoading: Ref<boolean>;
	resetForm: () => void;
	submitNote: () => Promise<void>;
}

const createDefaultFormData = (
	initialPlantingIds: number[] = [],
): NoteFormData => ({
	plantingIds: [...initialPlantingIds],
	description: "",
	photos: [],
	coordinates: [],
	isActionRequired: false,
	observations: [],
});

export interface NoteFormOptions {
	initialPlantingIds?: number[];
}

export function useNoteForm(options: NoteFormOptions = {}): NoteFormContext {
	const { initialPlantingIds = [] } = options;
	const { t } = useI18n();
	const { createNote, isLoading } = useCreateNote();
	const { addPhotosToNote } = useAddPhotosToNote();
	const { mutateAsync: getUploadUrls } = useGetNotePhotoUploadUrls();
	const { uploadPhotosToAzureInBackground } = usePhotoUpload();

	const formData = reactive<NoteFormData>(
		createDefaultFormData(initialPlantingIds),
	);

	const isValid = computed(
		() =>
			formData.description.trim().length > 0 ||
			formData.observations.length > 0,
	);

	const resetForm = () => {
		Object.assign(
			formData,
			createDefaultFormData(options.initialPlantingIds),
		);
	};

	const submitNote = async (): Promise<void> => {
		// 1. Store photos BEFORE any modal operations
		const photosToUpload = [...formData.photos];

		// 2. Create the note
		const noteData: CreateNoteDTO = {
			description: formData.description.trim(),
			location: coordsToGeoJSON(formData.coordinates),
			attributes: {
				photoCount: photosToUpload.length,
			},
			associations: formData.plantingIds.map((id) => ({
				entityType: EntityType.Planting,
				entityId: id,
			})),
			isActionRequired: formData.isActionRequired,
			observations: formData.observations.map((obs) => ({
				categoryId: obs.categoryId,
				subcategoryId: obs.subcategoryId,
				severity: obs.severity,
			})),
		};

		try {
			const noteId = await createNote(noteData);
			await sendToast(
				t("note.note-created-successfully"),
				2500,
				"success",
			);

			// 3. Upload photos in background (don't block on this)
			if (photosToUpload.length > 0) {
				// Fire-and-forget: Get URLs, upload files to Azure, then save metadata to our backend
				// If this fails, note is still created successfully
				getUploadUrls(photosToUpload)
					.then((uploadUrls) => {
						// Combine photos with their upload URLs into a single array
						const photosWithUrls = photosToUpload.map(
							(photo, index) => ({
								photo,
								uploadUrls: uploadUrls[index],
							}),
						);

						uploadPhotosToAzureInBackground(
							photosWithUrls,
							async (uploadedPhotos) => {
								// Map uploaded photos to NotePhotoDTO format
								const notePhotos = uploadedPhotos.map(
									(photo, index) => ({
										id: photo.photoId,
										name: photo.filename,
										fullSizeUrl: photo.fullSizeUrl,
										thumbnailUrl:
											photo.thumbnailUrl ||
											photo.fullSizeUrl,
										index,
									}),
								);

								// Save photo metadata to backend
								await addPhotosToNote({
									noteId,
									photos: notePhotos,
								});
							},
							(uploadError) => {
								// Silently log error - note is already created
								console.error(
									"Photo upload to Azure failed:",
									uploadError,
								);
								captureException(uploadError);
							},
						);
					})
					.catch((urlError) => {
						// Failed to get upload URLs from backend
						console.error(
							"Failed to get photo upload URLs:",
							urlError,
						);
						captureException(urlError);
					});
			}
		} catch (error) {
			captureException(error);
			await sendToast(t("note.failed-to-create-note"), 3000, "danger");
		}
	};

	return {
		formData,
		isValid,
		isLoading,
		resetForm,
		submitNote,
	};
}
